import {
  AnalyticsFormatHintInput,
  AnalyticsReportFiltersInput,
  AnalyticsWidgetInput,
  CreateAnalyticsReportDocument,
  CreateAnalyticsReportMutationVariables,
  UpdateAnalyticsReportDocument,
  UpdateAnalyticsReportMutationVariables,
} from 'generated/resources/graphql'
import { v4 as uuidv4 } from 'uuid'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

export type ReportToSave = {
  id?: string
  name: string
  datasourceId: string
  description: string
  widgets: AnalyticsWidgetInput[]
  filters: AnalyticsReportFiltersInput
  columnFormatHints: AnalyticsFormatHintInput[]
  columnOrder?: string[] | null
}

export type SaveReportArgs = {
  onSaveSuccess?: (reportId: string) => void
}

export const useSaveReport = ({ onSaveSuccess }: SaveReportArgs) => {
  const { company } = useCurrentSession()
  const [createReportMutation] = useMutation(CreateAnalyticsReportDocument, {
    extractorFn: (data) => data.analyticsMutations.createAnalyticsReport,
  })

  const [updateReportMutation] = useMutation(UpdateAnalyticsReportDocument, {
    extractorFn: (data) => data.analyticsMutations.updateAnalyticsReport,
  })

  const saveReport = async (report: ReportToSave) => {
    const isCreate = report.id === undefined

    const reportToSave:
      | CreateAnalyticsReportMutationVariables
      | UpdateAnalyticsReportMutationVariables = {
      id: report.id ?? uuidv4(),
      companyId: company.id,
      name: report.name.trim(),
      datasourceId: report.datasourceId,
      description: (report.description ?? '').trim().substring(0, 100),
      widgets: report.widgets.map((widget) => ({
        name: widget.name,
        description: widget.description,
        position: {
          height: widget.position.height,
          width: widget.position.width,
          x: widget.position.x,
          y: widget.position.y,
        },
        query: widget.query,
      })),
      filters: report.filters,
      columnFormatHints: report.columnFormatHints,
      columnOrder: report.columnOrder,
    }

    const handled = isCreate
      ? handleResult(
          await createReportMutation({
            variables: reportToSave,
          }),
          (_errors) => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('ai_reports.notifications.save_error'),
            })
          }
        )
      : handleResult(
          await updateReportMutation({
            variables: reportToSave,
          }),
          (_errors) => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('ai_reports.notifications.save_error'),
            })
          }
        )

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ai_reports.notifications.saved_successfully'),
      })

      onSaveSuccess?.(reportToSave.id)
    }
  }

  return {
    saveReport,
  }
}
